import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Role } from '../types/role';
import { ModulePermissions, RolePermissionsResponse, UpdateRolePermissionsData } from '../types/permission';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Permissions: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [rolePermissions, setRolePermissions] = useState<ModulePermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await api.roles.getAll();
      setRoles(response.data.data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async (roleId: string) => {
    try {
      setPermissionsLoading(true);
      const response = await api.permissions.getRolePermissions(roleId);
      const data: RolePermissionsResponse = response.data;
      setRolePermissions(data.data);
    } catch (error: any) {
      console.error('Error fetching role permissions:', error);
      toast.error('Failed to fetch role permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole.id.toString());
    }
  }, [selectedRole]);

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role);
  };

  const handlePermissionToggle = (moduleIndex: number, permissionIndex: number) => {
    const updatedPermissions = [...rolePermissions];
    updatedPermissions[moduleIndex].permissions[permissionIndex].is_granted = 
      !updatedPermissions[moduleIndex].permissions[permissionIndex].is_granted;
    setRolePermissions(updatedPermissions);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    setUpdating(true);
    try {
      const permissions: UpdateRolePermissionsData = {
        permissions: []
      };

      rolePermissions.forEach(module => {
        module.permissions.forEach(permission => {
          permissions.permissions.push({
            permission_id: permission.permission_id,
            is_granted: Boolean(permission.is_granted)
          });
        });
      });

      await api.permissions.updateRolePermissions(selectedRole.id.toString(), permissions);
      toast.success('Permissions updated successfully');
      
      // Refresh the permissions
      await fetchRolePermissions(selectedRole.id.toString());
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      toast.error('Failed to update permissions');
    } finally {
      setUpdating(false);
    }
  };

  const getPermissionCount = (role: Role) => {
    // This would need to come from the API response
    return role.permissions_count || 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Permissions Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage role-based permissions for system modules
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Roles List */}
        <div className="lg:col-span-1">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Roles</h3>
            <div className="space-y-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => handleRoleSelect(role)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedRole?.id === role.id
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{role.name}</div>
                      <div className="text-sm text-gray-500">
                        {getPermissionCount(role)} permissions
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        role.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {role.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Permissions Grid */}
        <div className="lg:col-span-3">
          {selectedRole ? (
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Permissions for {selectedRole.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Toggle permissions for this role across different modules
                  </p>
                </div>
                <button
                  onClick={handleSavePermissions}
                  disabled={updating || permissionsLoading}
                  className="btn btn-primary"
                >
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {permissionsLoading ? (
                <div className="flex justify-center items-center h-64">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-6">
                  {rolePermissions.map((module, moduleIndex) => (
                    <div key={module.module_id} className="border border-gray-200 rounded-lg">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <h4 className="text-md font-medium text-gray-900">
                          {module.module_name}
                        </h4>
                        {module.module_description && (
                          <p className="text-sm text-gray-500 mt-1">
                            {module.module_description}
                          </p>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {module.permissions.map((permission, permissionIndex) => (
                            <div
                              key={permission.permission_id}
                              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                            >
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`permission-${permission.permission_id}`}
                                    checked={Boolean(permission.is_granted)}
                                    onChange={() => handlePermissionToggle(moduleIndex, permissionIndex)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <label
                                    htmlFor={`permission-${permission.permission_id}`}
                                    className="ml-3 block text-sm font-medium text-gray-900 cursor-pointer"
                                  >
                                    {permission.permission_name}
                                  </label>
                                </div>
                                <div className="mt-1">
                                  <div className="text-xs text-gray-500 font-mono">
                                    {permission.permission_key}
                                  </div>
                                  {permission.description && (
                                    <div className="text-xs text-gray-600 mt-1">
                                      {permission.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-3">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  permission.is_active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {permission.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {module.permissions.length === 0 && (
                          <div className="text-center py-8">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No permissions found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              This module has no permissions defined yet.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {rolePermissions.length === 0 && (
                    <div className="text-center py-12">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No modules found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No modules with permissions are available.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.239" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Select a Role</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Choose a role from the list to manage its permissions.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Permissions;
